import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-oauth-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="oauth-callback-container">
      <div class="spinner">
        <div class="bounce1"></div>
        <div class="bounce2"></div>
        <div class="bounce3"></div>
      </div>
      <p>{{ statusMessage }}</p>
    </div>
  `,
  styles: [
    `
      .oauth-callback-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100vh;
        background-color: #f3f4f6;
        padding: 20px;
      }
      .spinner {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 20px;
      }
      .spinner > div {
        width: 18px;
        height: 18px;
        margin: 0 5px;
        background-color: #3859e0;
        border-radius: 100%;
        display: inline-block;
        animation: bounce 1.4s infinite ease-in-out both;
      }
      .spinner .bounce1 {
        animation-delay: -0.32s;
      }
      .spinner .bounce2 {
        animation-delay: -0.16s;
      }
      @keyframes bounce {
        0%,
        80%,
        100% {
          transform: scale(0);
        }
        40% {
          transform: scale(1);
        }
      }
      p {
        font-size: 16px;
        color: #374151;
      }
    `,
  ],
})
export class OAuthCallbackComponent implements OnInit {
  statusMessage = 'Completing authentication...';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const currentUrl = this.router.url;

    if (currentUrl.includes('/oauth-success')) {
      this.handleSecureOAuthFlow();
    } else {
      this.handleLegacyOAuthFlow();
    }
  }

  private handleSecureOAuthFlow(): void {
    this.authService.getOAuthToken().subscribe({
      next: (response) => {
        if (response.status === 'success' && response.token) {
          this.sendTokenToParent(response.token);
        } else {
          this.handleError('Authentication failed');
        }
      },
      error: (error) => {
        this.handleError('Authentication error');
      },
    });
  }

  private handleLegacyOAuthFlow(): void {
    this.route.queryParams.subscribe((params) => {
      const token = params['token'];

      if (token) {
        this.sendTokenToParent(token);
      } else {
        this.handleError('No token received');
      }
    });
  }

  private sendTokenToParent(token: string): void {
    if (window.opener && !window.opener.closed) {
      try {
        const targetOrigin = window.location.origin;

        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            try {
              window.opener.postMessage(
                {
                  type: 'OAUTH_TOKEN',
                  token: token,
                },
                targetOrigin
              );
            } catch (e) {}
          }, i * 100);
        }
      } catch (error) {}
    }

    try {
      localStorage.setItem('oauth_token', token);
      localStorage.setItem('oauth_timestamp', Date.now().toString());
    } catch (e) {}

    if ('BroadcastChannel' in window) {
      try {
        const channel = new BroadcastChannel('oauth_channel');
        channel.postMessage({
          type: 'OAUTH_TOKEN',
          token: token,
        });
        channel.close();
      } catch (e) {}
    }

    this.statusMessage = 'Authentication successful! Closing...';

    setTimeout(() => {
      window.close();

      setTimeout(() => {
        this.statusMessage =
          'Authentication complete! Please close this window.';
      }, 500);
    }, 1500);
  }

  private handleError(message: string): void {
    this.statusMessage = message;

    setTimeout(() => {
      window.close();

      setTimeout(() => {
        window.location.href = '/login';
      }, 500);
    }, 2000);
  }
}
